import {
  Breadcrumbs,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getToken,
  requestErrorHandler,
  tableAlternateColors,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import AddFeatureList from "./AddFeatureList";

const server = EnvSettings.server;

const transformFeatureLists = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const grouped = new Map();
  items.forEach((entry, index) => {
    const listName = entry?.ManufacturingFeatureListName;
    if (!listName?.id) {
      return;
    }
    const existing = grouped.get(listName.id);
    const featureData = entry?.ManufacturingFeature;
    const normalizedFeature = featureData
      ? {
          id: featureData.id ?? null,
          name: featureData.name || "",
          description: featureData.description || "",
          StockItem: featureData.StockItem || null,
        }
      : null;
    if (existing) {
      if (normalizedFeature) {
        existing.features.push(normalizedFeature);
      }
    } else {
      grouped.set(listName.id, {
        id: listName.id,
        name: listName.name || "",
        description: listName.description || "",
        StockItem: listName.StockItem || null,
        updatedAt: listName.updatedAt || entry.updatedAt || null,
        features: normalizedFeature ? [normalizedFeature] : [],
        orderIndex: index,
      });
    }
  });
  return Array.from(grouped.values());
};

export default function ManufacturingFeatureLists() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [featureLists, setFeatureLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [featureListToEdit, setFeatureListToEdit] = useState(null);
  const searchRef = useRef(null);

  const fetchFeatureLists = useCallback(() => {
    setIsLoading(true);
    setErrorMessage("");
    fetch(server + "/get_manufacturing_recipes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setFeatureLists([]);
          setErrorMessage(
            result.message ||
              t(
                "manufacturing.featureListsFetchFailed",
                "Failed to fetch feature lists."
              )
          );
        } else {
          const transformed = transformFeatureLists(
            result.manufacturingFeatureLists
          );
          setFeatureLists(transformed);
          setErrorMessage("");
        }
      })
      .catch(() => {
        setFeatureLists([]);
        setErrorMessage(
          t(
            "manufacturing.featureListsFetchFailed",
            "Failed to fetch feature lists."
          )
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, t]);

  useEffect(() => {
    fetchFeatureLists();
  }, [fetchFeatureLists]);

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setFilteredLists(featureLists);
      return;
    }
    const filtered = featureLists.filter((list) => {
      const fields = [
        list.name,
        list.description,
        list.StockItem?.name,
        list.StockItem?.sku,
        ...(list.features || []).map((feature) => feature.name),
      ].flat();
      return fields.some((field) => {
        if (!field && field !== 0) {
          return false;
        }
        return field.toString().toLowerCase().includes(term);
      });
    });
    setFilteredLists(filtered);
  }, [featureLists, searchValue]);

  const featureListHeadCells = useMemo(
    () => [
      {
        id: "name",
        label: t("manufacturing.featureListName", "Feature list name"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "StockItem.sku",
        label: t("stock.sku", "Linked stock item"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "featureCount",
        label: t("manufacturing.features", "Features"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "updatedAt",
        label: t("updatedAt", "Updated"),
        disablePadding: false,
        numeric: false,
      },
    ],
    [t]
  );

  const tableData = useMemo(
    () =>
      filteredLists.map((list) => ({
        ...list,
        featureCount: list.features?.length ?? 0,
      })),
    [filteredLists]
  );

  const specialFormatting = useMemo(
    () => [
      {
        id: "featureCount",
        format: (value) => value?.toString() ?? "0",
      },
      {
        id: "updatedAt",
        format: (value) => (value ? formatTimeWithTZOffset(value, ECABIN) : ""),
      },
    ],
    []
  );

  return (
    <Grid container spacing={3}>
      <AddFeatureList
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
          setFeatureListToEdit(null);
        }}
        onCompleted={() => {
          setShowDialog(false);
          setFeatureListToEdit(null);
          fetchFeatureLists();
        }}
        initialFeatureList={featureListToEdit}
      />
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/manufacturing">
            {t("manufacturing.header", "Manufacturing")}
          </Link>
          <Typography color="textPrimary">
            {t("manufacturing.featureLists", "Feature lists")}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setFeatureListToEdit(null);
                  setShowDialog(true);
                }}
              >
                {t("manufacturing.addFeatureList", "Add feature list")}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  history.push("/cpanel/manufacturing/features/items")
                }
              >
                {t("manufacturing.viewFeatureItems", "View feature items")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Title>{t("manufacturing.featureLists", "Feature lists")}</Title>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("common.search", "Search")}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                inputRef={searchRef}
              />
            </Grid>
          </Grid>
          {errorMessage ? (
            <Grid container style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{ padding: 16, background: "#ffeeee" }}
                >
                  {errorMessage}
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {isLoading ? (
            <CircularProgress />
          ) : tableData.length ? (
            <SortableTable
              tableLayout="fixed"
              downloadButton
              header={t("manufacturing.featureLists", "Feature lists")}
              headCells={featureListHeadCells}
              tableData={tableData}
              rowsPerPage={25}
              defaultSortRow="name"
              alternateColors
              getRowStyle={tableAlternateColors}
              specialDataFormatting={specialFormatting}
              handleRowClick={(row) => {
                setFeatureListToEdit(row);
                setShowDialog(true);
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              {searchValue
                ? t(
                    "manufacturing.noFeatureListsMatchSearch",
                    "No feature lists match your search."
                  )
                : t(
                    "manufacturing.noFeatureListsFound",
                    "No feature lists available."
                  )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
